"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  lendPhoneNumberAction,
  returnPhoneNumberLoanAction,
  takeOverPhoneNumberLoanAction,
} from "@/server-actions/phone-number-loans"
import { withQueryHandling } from "@/lib/with-query-handling"
import { pbxPhoneNumbersQueryKey } from "@/hooks/queries/pbx-phone-numbers"
import { outgoingVoiceCallPhoneNumbersQueryKey } from "@/hooks/queries/outgoing-voice-calls"
import { campaignsQueryKey } from "@/hooks/queries/campaigns"

export function useLendPhoneNumberMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { phoneNumber: string; destinationTenantId: string; sourceTenantId?: string }) => {
      const result = await withQueryHandling(lendPhoneNumberAction(input), {
        loading: "Lending Demo number...",
        success: "Demo number lent",
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: () => invalidateLoanQueries(queryClient),
  })
}

export function useReturnPhoneNumberLoanMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { loanId: string }) => {
      return withQueryHandling(returnPhoneNumberLoanAction(input), {
        loading: "Returning Demo number...",
        success: "Demo number returned",
      })
    },
    onSuccess: () => invalidateLoanQueries(queryClient),
  })
}

export function useTakeOverPhoneNumberLoanMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      loanId: string
      phoneNumber: string
      sourceTenantId: string
      destinationTenantId: string
    }) => {
      const result = await withQueryHandling(takeOverPhoneNumberLoanAction(input), {
        loading: "Taking away and lending Demo number...",
        success: "Demo number lent here",
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: () => invalidateLoanQueries(queryClient),
  })
}

async function invalidateLoanQueries(queryClient: ReturnType<typeof useQueryClient>) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: pbxPhoneNumbersQueryKey }),
    queryClient.invalidateQueries({ queryKey: outgoingVoiceCallPhoneNumbersQueryKey }),
    queryClient.invalidateQueries({ queryKey: campaignsQueryKey }),
  ])
}
